/**
 * Temporal status for appointments (#2238).
 *
 * The calendar normalizer used to hard-code `status: "upcoming"`, so a
 * consumer acting on the normalized item showed running or finished
 * appointments as future. Status is computed from a supplied clock against
 * the appointment's OWN timezone (appointments store wall-clock times; a
 * 09:00 Tokyo consult must not become "upcoming" when viewed from Paris at
 * 08:00 local).
 */

import { serializeMedicationLogScheduledFor } from "./medication-log-date";

export type AppointmentTemporalStatus = "past" | "current" | "upcoming";

export type AppointmentTemporalFields = {
  startDate: string;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  timeZone?: string | null;
};

/** Fallback span for appointments that carry no end information. */
const DEFAULT_APPOINTMENT_MINUTES = 60;

function instantOf(
  date: string,
  time: string,
  timeZone: string | null | undefined,
): number | null {
  // Wall-clock in the appointment's zone → UTC instant (DST-safe two-pass
  // conversion shared with the medication-log serializer).
  const iso = serializeMedicationLogScheduledFor(
    `${date}T${time}:00`,
    timeZone ?? undefined,
  );
  if (!iso) return null;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? null : value;
}

/**
 * Compute past/current/upcoming from a supplied clock.
 *
 * - a missing start time treats the appointment as all-day: current for the
 *   whole of startDate in its own timezone;
 * - a missing end falls back to endDate's end-of-day when endDate exists,
 *   else one hour after the start;
 * - cross-midnight spans (endDate after startDate) stay current through the
 *   real end instant.
 */
export function computeAppointmentStatus(
  appointment: AppointmentTemporalFields,
  now: Date,
): AppointmentTemporalStatus {
  const tz = appointment.timeZone;
  const allDay = !appointment.startTime;

  const start = instantOf(
    appointment.startDate,
    appointment.startTime || "00:00",
    tz,
  );
  if (start === null) return "upcoming";

  let end: number | null = null;
  if (appointment.endDate) {
    end = instantOf(
      appointment.endDate,
      appointment.endTime || "23:59",
      tz,
    );
  } else if (allDay) {
    end = instantOf(appointment.startDate, "23:59", tz);
  } else if (appointment.endTime) {
    end = instantOf(appointment.startDate, appointment.endTime, tz);
  }
  if (end === null) {
    end = start + DEFAULT_APPOINTMENT_MINUTES * 60_000;
  }

  const at = now.getTime();
  if (at < start) return "upcoming";
  if (at <= end) return "current";
  return "past";
}
